/**
 * NotificationsBell — header icon + slide-out drawer.
 *
 * Polls `/api/me/notifications/unread-count` every 30s so the badge
 * stays fresh without WebSockets. On click, opens a Shadcn `Sheet`
 * showing the 30 most-recent notifications grouped by today / this
 * week / older. Each notification:
 *   • Clicking the body deep-links to `link` (if present) and marks read.
 *   • "Mark all read" button at the top of the drawer.
 *   • Empty state pitches what notifications will surface here.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { BellIcon as Bell, TruckIcon as Truck, ChatCircleDotsIcon as ChatCircleDots, HeartStraightIcon as HeartStraight, StorefrontIcon as Storefront, ShieldStarIcon as ShieldStar, CheckCircleIcon as CheckCircle, CalendarIcon as Calendar, ArrowRightIcon as ArrowRight, TrashIcon as Trash, SparkleIcon as Sparkle, ArrowsLeftRightIcon as ArrowsLeftRight, CurrencyDollarIcon as CurrencyDollar, VideoCameraIcon as VideoCamera, TreeIcon as Tree, BankIcon as Bank } from "@phosphor-icons/react";
import { formatDate } from "@/lib/utils-app";

// Polling cadence — modest to keep mobile data lean.
const POLL_MS = 30_000;

// Phosphor icon resolver — backend ships `icon` as a string. We map
// the well-known names; unknown values fall back to Bell.
const ICONS = {
  Bell, Truck, ChatCircleDots, HeartStraight, Storefront, ShieldStar,
  CheckCircle, Calendar, Sparkle,
  ArrowsLeftRight, CurrencyDollar, VideoCamera, Tree, Bank,
};

const TONE_CLS = {
  crimson: "bg-[#FEE2E2] text-[#C1272D]",
  gold:    "bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]",
  emerald: "bg-[#DCFCE7] text-[#15803D]",
  ink:     "bg-ink/5 text-ink",
};

function bucketRow(createdAtIso, now) {
  if (!createdAtIso) return "older";
  const t = new Date(createdAtIso).getTime();
  if (Number.isNaN(t)) return "older";
  const delta = now - t;
  if (delta < 86_400_000) return "today";       // 24h
  if (delta < 7 * 86_400_000) return "week";
  return "older";
}

export default function NotificationsBell({ user }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Lightweight unread polling (badge only) — runs whether or not the
  // drawer is open so the badge stays accurate.
  const pollUnread = useCallback(() => {
    if (!user) return;
    api.get("/me/notifications/unread-count", { silent: true })
      .then((r) => setUnread(r.data?.unread_count || 0))
      .catch(() => { /* swallow */ });
  }, [user]);

  useEffect(() => {
    pollUnread();
    if (!user) return;
    const t = setInterval(pollUnread, POLL_MS);
    return () => clearInterval(t);
  }, [user, pollUnread]);

  // Lazy-load the full list when the drawer opens.
  const fetchList = useCallback(() => {
    setLoading(true);
    api.get("/me/notifications?limit=30", { silent: true })
      .then((r) => {
        setItems(r.data?.items || []);
        setUnread(r.data?.unread_count || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const markAllRead = async () => {
    try {
      await api.post("/me/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch { /* swallow */ }
  };

  const onItemClick = async (n) => {
    if (!n.read_at) {
      try {
        await api.post(`/me/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* swallow */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const deleteOne = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/me/notifications/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch { /* swallow */ }
  };

  const now = Date.now();
  const grouped = { today: [], week: [], older: [] };
  items.forEach((it) => grouped[bucketRow(it.created_at, now)].push(it));

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="notifications-bell"
        aria-label={unread > 0 ? `${unread} unread notifications` : "Notifications"}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-full text-[#3F3F3F] hover:text-ink hover:bg-[#FAFAFA] transition-colors"
      >
        <Bell size={18} weight={unread > 0 ? "fill" : "duotone"} />
        {unread > 0 && (
          <span
            data-testid="notifications-bell-badge"
            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C1272D] text-white text-[10px] font-bold leading-none ring-2 ring-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col"
          data-testid="notifications-drawer"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#E5E5E5]/70">
            <div className="flex items-baseline justify-between gap-3">
              <SheetTitle className="font-spectral text-2xl tracking-tight">
                Notifications
              </SheetTitle>
              {unread > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={markAllRead}
                  data-testid="notifications-mark-all-read"
                  className="text-[11px] font-semibold text-ink hover:text-crimson h-7 px-2"
                >
                  Mark all read
                </Button>
              )}
            </div>
            <SheetDescription className="text-left text-[#6B6B6B] text-[12px]">
              {unread > 0
                ? `${unread} unread`
                : "You're all caught up."}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="text-sm text-[#6B6B6B] py-10 text-center">Loading…</p>
            ) : items.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                <Bucket label="Today" rows={grouped.today} onClick={onItemClick} onDelete={deleteOne} />
                <Bucket label="This week" rows={grouped.week} onClick={onItemClick} onDelete={deleteOne} />
                <Bucket label="Older" rows={grouped.older} onClick={onItemClick} onDelete={deleteOne} />
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Bucket({ label, rows, onClick, onDelete }) {
  if (!rows.length) return null;
  return (
    <div data-testid={`notifications-bucket-${label.toLowerCase().replace(" ","-")}`}>
      <div className="px-5 pt-4 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] bg-[#FAFAFA]">
        {label}
      </div>
      <div className="divide-y divide-[#EAEAEA]">
        {rows.map((n) => <NotificationRow key={n.id} n={n} onClick={onClick} onDelete={onDelete} />)}
      </div>
    </div>
  );
}

function NotificationRow({ n, onClick, onDelete }) {
  const Icon = ICONS[n.icon] || Bell;
  const toneCls = TONE_CLS[n.tone] || TONE_CLS.ink;
  return (
    <button
      type="button"
      onClick={() => onClick(n)}
      data-testid={`notification-row-${n.id}`}
      className={`group w-full px-5 py-3 flex items-start gap-3 text-left hover:bg-[#FAFAFA] transition-colors ${
        n.read_at ? "opacity-70" : ""
      }`}
    >
      <span className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full flex-shrink-0 ${toneCls}`}>
        <Icon size={16} weight="duotone" />
        {!n.read_at && (
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#C1272D] ring-2 ring-white" />
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-ink leading-tight">
          {n.title}
        </span>
        {n.body && (
          <span className="block text-[12px] text-[#3F3F3F] mt-0.5 leading-relaxed">
            {n.body}
          </span>
        )}
        <span className="block text-[10px] text-[#9CA3AF] mt-1 font-tabular">
          {timeAgo(n.created_at)}
        </span>
      </span>
      <div className="flex flex-col items-end gap-1 flex-shrink-0 pt-1">
        {n.link && (
          <ArrowRight size={12} weight="bold" className="text-[#9CA3AF] group-hover:text-ink transition-colors" />
        )}
        <button
          type="button"
          onClick={(e) => onDelete(e, n.id)}
          aria-label="Dismiss"
          className="opacity-0 group-hover:opacity-100 hover:text-crimson text-[#9CA3AF] transition-opacity p-1"
          data-testid={`notification-delete-${n.id}`}
        >
          <Trash size={12} />
        </button>
      </div>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-12 text-center" data-testid="notifications-empty">
      <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#FBF7EC] mb-3">
        <Bell size={20} weight="duotone" className="text-gold-dark" />
      </span>
      <p className="font-spectral text-lg tracking-tight text-ink mb-2">No notifications yet</p>
      <p className="text-[12px] text-[#6B6B6B] leading-relaxed max-w-xs mx-auto">
        New bids, inquiries, transfers, escrow updates, and dog log reminders will land here.
      </p>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const delta = Math.max(0, Date.now() - t);
  const min = Math.floor(delta / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return formatDate(iso);
}
